/**
 * SCR-027 — Portal Progress /portal/progress
 * Published ProgressReport list + detail view with grade/attendance trend charts.
 * Comment list + form (authenticated). Export PDF/Excel client-side (FR-C3-15).
 * Mobile-first: 1-col layout; charts full-width.
 */
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { usePortalReports, usePortalReportDetail, useCreatePortalComment, useUpdatePortalComment } from '@client/lib/queries/use-portal';
import { useChildSelector } from '@client/store/child-selector';
import { authClient } from '@client/lib/auth-client';
import { ReportComments } from '@client/components/report/report-comments';
import { ReportExport } from '@client/components/report/report-export';

export const Route = createFileRoute('/portal/progress')({
  component: PortalProgress,
});

const RATING_COLORS: Record<string, string> = {
  excellent: 'bg-brand-green-100 text-brand-green-700 dark:bg-brand-green-900/50 dark:text-brand-green-300',
  good: 'bg-[var(--color-info)]/10 text-[var(--color-info)] dark:bg-[var(--color-info-border)]/15 dark:text-[var(--color-info-border)]',
  average: 'bg-brand-orange-100 text-brand-orange-700 dark:bg-brand-orange-950/50 dark:text-brand-orange-300',
  needs_improvement: 'bg-destructive/10 text-destructive',
};
const RATING_LABEL: Record<string, string> = {
  excellent: 'Xuất sắc', good: 'Tốt', average: 'Khá', needs_improvement: 'Cần cải thiện',
};

function PortalProgress() {
  const { data: session } = authClient.useSession();
  const user = session?.user as { id?: string; role?: string } | undefined;
  const role = user?.role ?? '';
  const userId = user?.id ?? '';
  const { selectedChildId } = useChildSelector();
  const studentId = role === 'parent' ? selectedChildId : undefined;

  const { data: reportsList, isLoading: listLoading, isError: listError, refetch: refetchList } =
    usePortalReports(studentId);

  const reports = reportsList?.reports ?? [];
  const [selectedReportId, setSelectedReportId] = useState<string>('');

  // Auto-select first report
  const effectiveReportId = selectedReportId || reports[0]?.id || '';

  const { data: detailData, isLoading: detailLoading, isError: detailError } =
    usePortalReportDetail(effectiveReportId);

  const createComment = useCreatePortalComment(effectiveReportId);
  const updateComment = useUpdatePortalComment(effectiveReportId);

  if (listLoading) return <ProgressSkeleton />;

  if (listError) {
    return (
      <div className="flex flex-col items-center gap-3 py-16 text-center">
        <AlertCircle className="h-8 w-8 text-destructive" />
        <p className="text-sm text-muted-foreground">Không tải được báo cáo</p>
        <Button variant="outline" size="sm" onClick={() => void refetchList()}>
          <RefreshCw className="h-4 w-4 mr-2" />Thử lại
        </Button>
      </div>
    );
  }

  if (reports.length === 0) {
    return (
      <div className="max-w-4xl mx-auto">
        <h1 className="text-xl font-bold mb-4">Báo cáo tiến độ</h1>
        <div className="py-16 text-center text-sm text-muted-foreground border border-dashed rounded-lg">
          Chưa có báo cáo tiến độ
        </div>
      </div>
    );
  }

  const report = detailData?.report;
  const trend = detailData?.trend;
  const comments = detailData?.comments ?? [];
  const studentName = reportsList?.student.fullName ?? '';

  return (
    <div className="space-y-5 max-w-4xl mx-auto">
      {/* Header + selector */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="text-xl font-bold">Báo cáo tiến độ</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <Select
            value={effectiveReportId}
            onValueChange={(v) => setSelectedReportId(v)}
          >
            <SelectTrigger className="w-full sm:w-64 h-9 text-sm">
              <SelectValue placeholder="Chọn kỳ báo cáo" />
            </SelectTrigger>
            <SelectContent>
              {reports.map((r) => (
                <SelectItem key={r.id} value={r.id}>
                  {r.periodLabel ?? r.period}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <ReportExport report={detailData} studentName={studentName} />
        </div>
      </div>

      {detailLoading && <Skeleton className="h-48 rounded-lg" />}

      {detailError && (
        <div className="flex items-center gap-2 text-sm text-destructive border border-destructive/30 rounded-lg p-3">
          <AlertCircle className="h-4 w-4 shrink-0" />
          Không tải được chi tiết báo cáo
        </div>
      )}

      {report && (
        <>
          {/* Report header card */}
          <Card className="border-border">
            <CardHeader className="pb-2 pt-4 px-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <CardTitle className="text-base">{report.periodLabel ?? report.period}</CardTitle>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {report.className} · {report.courseName}
                  </p>
                  {report.periodStart && report.periodEnd && (
                    <p className="text-xs text-muted-foreground">
                      {report.periodStart} → {report.periodEnd}
                    </p>
                  )}
                </div>
                {report.overallRating && (
                  <span className={`text-sm font-semibold px-3 py-1 rounded-full ${RATING_COLORS[report.overallRating] ?? ''}`}>
                    {RATING_LABEL[report.overallRating] ?? report.overallRating}
                  </span>
                )}
              </div>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-3">
              {report.attendanceSummary && (
                <ReportField label="Chuyên cần" value={report.attendanceSummary} />
              )}
              {report.academicComment && (
                <ReportField label="Nhận xét học lực" value={report.academicComment} />
              )}
              {report.behaviorComment && (
                <ReportField label="Nhận xét hành vi" value={report.behaviorComment} />
              )}
              {report.recommendation && (
                <ReportField label="Khuyến nghị" value={report.recommendation} />
              )}
            </CardContent>
          </Card>

          {/* Trend charts — side by side on md+ */}
          {trend && (trend.grades.length > 0 || trend.attendance.length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {trend.grades.length > 0 && (
                <Card className="border-border">
                  <CardHeader className="pb-2 pt-3 px-4">
                    <CardTitle className="text-sm font-semibold">Xu hướng điểm số</CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 pb-4">
                    <ResponsiveContainer width="100%" height={180}>
                      <LineChart data={trend.grades.map((g) => ({
                        name: g.label,
                        score: Math.round((g.score / g.maxScore) * 100),
                        date: g.gradedAt ? new Date(g.gradedAt).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' }) : '',
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
                        <Tooltip formatter={(v) => [`${v as number}%`, 'Điểm']} />
                        <Line type="monotone" dataKey="score" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 4 }} />
                      </LineChart>
                    </ResponsiveContainer>
                    {trend.grades.length === 1 && (
                      <p className="text-xs text-muted-foreground text-center mt-1">
                        (Chỉ có 1 điểm — chưa có đủ dữ liệu để vẽ đường xu hướng)
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}
              {trend.attendance.length > 0 && (
                <Card className="border-border">
                  <CardHeader className="pb-2 pt-3 px-4">
                    <CardTitle className="text-sm font-semibold">Chuyên cần theo buổi</CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 pb-4">
                    <ResponsiveContainer width="100%" height={180}>
                      <LineChart data={trend.attendance.map((a) => ({
                        name: a.label,
                        rate: Math.round(a.rate * 100),
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                        <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
                        <Tooltip formatter={(v) => [`${v as number}%`, 'Có mặt']} />
                        <Line type="monotone" dataKey="rate" stroke="var(--color-brand-green-500)" strokeWidth={2} dot={{ r: 4 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Comments */}
          <Card className="border-border">
            <CardContent className="px-4 py-4">
              <ReportComments
                comments={comments}
                reportId={effectiveReportId}
                readOnly={false}
                currentUserId={userId}
                onSubmit={async (content) => {
                  await createComment.mutateAsync({ content });
                }}
                onEdit={async (commentId, content) => {
                  await updateComment.mutateAsync({ commentId, content });
                }}
              />
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function ReportField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-0.5">{label}</p>
      <p className="text-sm text-foreground whitespace-pre-wrap">{value}</p>
    </div>
  );
}

function ProgressSkeleton() {
  return (
    <div className="space-y-4 max-w-4xl mx-auto">
      <Skeleton className="h-7 w-40" />
      <Skeleton className="h-10 w-64" />
      <Skeleton className="h-48 rounded-lg" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Skeleton className="h-52 rounded-lg" />
        <Skeleton className="h-52 rounded-lg" />
      </div>
    </div>
  );
}
